import './Payment.scss';

import { type FC, useState, useMemo, useCallback } from 'react';
import { Formik, FormikErrors } from 'formik';
import { gql, useMutation, useReactiveVar } from '@apollo/client';
import classNames from 'classnames';
import { uniq } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import {
  FontLicenseType,
  UpdateCurrentOrderMutation,
  UpdateCurrentOrderMutationVariables,
  CompleteCurrentOrderMutation,
  CompleteCurrentOrderMutationVariables,
} from 'client/graphql/types/operations';

import { Redirect } from 'components/Redirect';
import useLanguage from 'utils/useLanguage';
import * as state from 'state';
import useCurrentUserQuery from 'queries/useCurrentUserQuery';
import useCurrentOrderQuery, {
  CURRENT_ORDER_FRAGMENT,
} from 'queries/useCurrentOrderQuery';
import * as Core from 'components/core';
import * as Braintree from 'components/braintree';
import ColorBar from 'components/ColorBar';
import CreditCardFields from './CreditCardFields';
import AddressFields from './AddressFields';
import LicenseeInformation from './LicenseeInformation';
import StudentLicenseDetails from './StudentLicenseDetails';
import OrderDetails from './OrderDetails';
import PaymentMethodSelect from './PaymentMethodSelect';
import { stringIsPresent } from 'utils/validation';

import { PaymentAuthorization, PaymentMethod } from 'types';
import { Values } from './types';
import { reportError } from 'client/utils/reportError';

const UPDATE_CURRENT_ORDER_MUTATION = gql`
  mutation UpdateCurrentOrderMutation(
    $input: UpdateCurrentOrderInput!
    $language: Language!
  ) {
    updateCurrentOrder(input: $input) {
      order {
        ...CurrentOrderFragment
      }

      success

      errors {
        attribute
        message
      }
    }
  }

  ${CURRENT_ORDER_FRAGMENT}
`;

const COMPLETE_CURRENT_ORDER_MUTATION = gql`
  mutation CompleteCurrentOrderMutation(
    $input: CompleteCurrentOrderInput!
    $language: Language!
  ) {
    completeCurrentOrder(input: $input) {
      order {
        ...CurrentOrderFragment
      }

      success

      errors {
        attribute
        message
      }
    }
  }

  ${CURRENT_ORDER_FRAGMENT}
`;

const CheckoutPayment: FC = () => {
  const { t } = useTranslation(['translation', 'checkout']);

  const language = useLanguage();

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);

  const [paypalPaymentAuthorization, setPaypalPaymentAuthorization] =
    useState<PaymentAuthorization | null>();

  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string | null>(
    null,
  );

  const hostedFields = Braintree.useHostedFields();

  const currentUserQuery = useCurrentUserQuery();

  const currentOrderQuery = useCurrentOrderQuery();

  const [updateCurrentOrder, updateCurrentOrderMutation] = useMutation<
    UpdateCurrentOrderMutation,
    UpdateCurrentOrderMutationVariables
  >(UPDATE_CURRENT_ORDER_MUTATION, {
    onCompleted: (data) => {
      if (data.updateCurrentOrder?.errors != null) {
        const error = data.updateCurrentOrder.errors.find(
          (e) => e.attribute == null,
        );

        if (error != null) {
          state.appendNotification({
            type: 'alert',
            content: error.message,
          });
        }
      }
    },
  });

  const [completeCurrentOrder, completeCurrentOrderMutation] = useMutation<
    CompleteCurrentOrderMutation,
    CompleteCurrentOrderMutationVariables
  >(COMPLETE_CURRENT_ORDER_MUTATION, {
    onCompleted: (data) => {
      if (data.completeCurrentOrder?.success) {
        state.lastCompletedOrderId(data.completeCurrentOrder.order?.id);
      } else if (data.completeCurrentOrder?.errors != null) {
        const error = data.completeCurrentOrder.errors.find(
          (e) => e.attribute == null,
        );

        if (error != null) {
          state.appendNotification({
            type: 'alert',
            content: error.message,
          });
        }
      }
    },
  });

  const checkoutScriptsLoaded = useReactiveVar(state.checkoutScriptsLoaded);

  const { currentUser } = currentUserQuery.data ?? {};
  const { currentOrder } = currentOrderQuery.data ?? {};

  const licenseTypes = uniq(
    currentOrder?.fontLicenses.map(({ licenseOption }) => licenseOption.type) ??
      [],
  );

  const initialValues = useMemo(() => {
    return {
      licenseeEmail: currentOrder?.licenseeEmail ?? '',
      licenseeName: currentOrder?.licenseeName ?? '',
      licenseeIsLegalEntity: currentOrder?.licenseeIsLegalEntity ?? null,
      addressName: currentOrder?.address?.name ?? '',
      addressCompany: currentOrder?.address?.company ?? '',
      addressLine1: currentOrder?.address?.line1 ?? '',
      addressLine2: currentOrder?.address?.line2 ?? '',
      addressLocality: currentOrder?.address?.locality ?? '',
      addressRegion: currentOrder?.address?.region ?? '',
      addressCountryCode: currentOrder?.address?.countryCode ?? '',
      addressPostalCode: currentOrder?.address?.postalCode ?? '',
      studentInstitutionName: currentOrder?.studentInstitutionName ?? '',
      studentProgramName: currentOrder?.studentProgramName ?? '',
      studentEnrolled: false,
      vatNumber: currentOrder?.vatNumber ?? '',
      promotionCode: '',
      licensesAgreedTo: false,
      termsAndConditionsAgreedTo: false,
      receiveNewsletter: false,
    };
  }, [currentOrder]);

  const validate = useCallback(
    (values: Values) => {
      const errors: FormikErrors<Values> = {};

      if (!stringIsPresent(values.addressName)) {
        errors.addressName = 'This field is required';
      }

      if (!stringIsPresent(values.addressLine1)) {
        errors.addressLine1 = 'This field is required';
      }

      if (!stringIsPresent(values.addressLocality)) {
        errors.addressLocality = 'This field is required';
      }

      if (!stringIsPresent(values.addressCountryCode)) {
        errors.addressCountryCode = 'This field is required';
      }

      if (!stringIsPresent(values.addressPostalCode)) {
        errors.addressPostalCode = 'This field is required';
      }

      if (!stringIsPresent(values.licenseeEmail)) {
        errors.licenseeEmail = 'This field is required';
      }

      if (!stringIsPresent(values.licenseeName)) {
        errors.licenseeName = 'This field is required';
      }

      if (values.licenseeIsLegalEntity == null) {
        errors.licenseeIsLegalEntity = 'This field is required';
      }

      if (licenseTypes.includes(FontLicenseType.Student)) {
        if (!stringIsPresent(values.studentInstitutionName)) {
          errors.studentInstitutionName = 'This field is required';
        }

        if (!values.studentEnrolled) {
          errors.studentEnrolled = 'This field is required';
        }
      }

      if (!values.licensesAgreedTo) {
        errors.licensesAgreedTo = 'You must agree to all licenses';
      }

      if (!values.termsAndConditionsAgreedTo) {
        errors.termsAndConditionsAgreedTo =
          'You must agree to the terms and conditions';
      }

      return errors;
    },
    [licenseTypes],
  );

  if (
    currentUserQuery.loading ||
    currentOrderQuery.loading ||
    !checkoutScriptsLoaded
  ) {
    return <Core.Loader />;
  }
  if (
    completeCurrentOrderMutation.data?.completeCurrentOrder?.order != null &&
    completeCurrentOrderMutation.data.completeCurrentOrder.success
  ) {
    return (
      <Redirect
        to={`/orders/${completeCurrentOrderMutation.data.completeCurrentOrder.order.number}`}
      />
    );
  }

  if (currentOrder == null) {
    return <Redirect to="/checkout/cart" />;
  }

  if (currentUser == null) {
    return <Redirect to="/checkout/register" />;
  }

  return (
    <ColorBar>
      <div className="content-wrapper">
        <Formik<Values>
          initialValues={initialValues}
          initialErrors={validate(initialValues)}
          validate={validate}
          onSubmit={async (values, { setSubmitting, setErrors }) => {
            let success = false;

            try {
              setPaymentErrorMessage(null);

              // The address and VAT number can change the order total, so we
              // need to update the order before creating the payment
              // authorization.
              const updateCurrentOrderResult = await updateCurrentOrder({
                variables: {
                  input: {
                    addressName: values.addressName,
                    addressCompany: values.addressCompany,
                    addressLine1: values.addressLine1,
                    addressLine2: values.addressLine2,
                    addressLocality: values.addressLocality,
                    addressRegion: values.addressRegion,
                    addressPostalCode: values.addressPostalCode,
                    addressCountryCode: values.addressCountryCode,
                    vatNumber: values.vatNumber,
                    licenseeIsLegalEntity: values.licenseeIsLegalEntity,
                  },
                  language,
                },
              });

              if (
                updateCurrentOrderResult.data?.updateCurrentOrder?.order == null
              ) {
                throw new Error('Failed to update current order');
              }

              let paymentAuthorization: PaymentAuthorization | null = null;

              if (currentOrder.total > 0) {
                try {
                  if (paymentMethod === 'credit-card') {
                    paymentAuthorization = await hostedFields.tokenize({
                      total:
                        updateCurrentOrderResult.data.updateCurrentOrder.order
                          .total,
                    });
                  } else if (
                    paymentMethod === 'paypal' &&
                    paypalPaymentAuthorization != null
                  ) {
                    paymentAuthorization = paypalPaymentAuthorization;
                  } else {
                    throw new Error(
                      'Invalid payment method or missing payment authorization',
                    );
                  }
                } catch (error) {
                  setPaymentErrorMessage(error.message);

                  throw error;
                }
              }

              const result = await completeCurrentOrder({
                variables: {
                  input: {
                    addressName: values.addressName,
                    addressCompany: values.addressCompany,
                    addressLine1: values.addressLine1,
                    addressLine2: values.addressLine2,
                    addressLocality: values.addressLocality,
                    addressRegion: values.addressRegion,
                    addressPostalCode: values.addressPostalCode,
                    addressCountryCode: values.addressCountryCode,
                    licenseeEmail: values.licenseeEmail,
                    licenseeName: values.licenseeName,
                    studentInstitutionName: values.studentInstitutionName,
                    studentProgramName: values.studentProgramName,
                    vatNumber: values.vatNumber,
                    promotionCode: values.promotionCode,
                    receiveNewsletter: values.receiveNewsletter,
                    paymentBraintreeNonce: paymentAuthorization?.nonce,
                  },
                  language,
                },
              });

              if (result.data?.completeCurrentOrder?.success) {
                success = true;
              } else {
                setErrors(
                  result.data?.completeCurrentOrder?.errors?.reduce(
                    (errors, error) => {
                      if (error.attribute != null) {
                        return {
                          ...errors,
                          [error.attribute]: error.message,
                        };
                      }
                      return errors;
                    },
                    {},
                  ) || {},
                );
              }
            } catch (error) {
              reportError(error);

              state.appendNotification({
                type: 'alert',
                content: t('payment.genericError', { ns: 'checkout' }),
              });
            } finally {
              if (!success) {
                setSubmitting(false);
              }
            }
          }}
        >
          {({
            handleSubmit,
            setFieldTouched,
            setFieldValue,
            submitForm,
            values,
            isValid,
            isSubmitting,
          }) => (
            <form
              className={classNames('CheckoutPayment', {
                'CheckoutPayment--acting': isSubmitting,
              })}
              onSubmit={handleSubmit}
            >
              <div className="CheckoutPayment-section">
                <h2>
                  {currentOrder.total > 0
                    ? t('payment.heading', { ns: 'checkout' })
                    : t('payment.addressHeading', { ns: 'checkout' })}
                </h2>
                {currentOrder.total > 0 && (
                  <>
                    {paymentErrorMessage != null && (
                      <div className="CheckoutPayment-error">
                        <p
                          dangerouslySetInnerHTML={{
                            __html: t('payment.error.message', {
                              ns: 'checkout',
                            }).toString(),
                          }}
                        />
                        <p
                          dangerouslySetInnerHTML={{
                            __html: t('payment.error.details', {
                              ns: 'checkout',
                            }).toString(),
                          }}
                        />
                      </div>
                    )}

                    <PaymentMethodSelect
                      value={paymentMethod}
                      disabled={isSubmitting}
                      onChange={(value) => {
                        setPaymentMethod(value);
                      }}
                    />

                    {paymentMethod === 'credit-card' && <CreditCardFields />}
                  </>
                )}

                <AddressFields disabled={isSubmitting} />
              </div>

              <div className="CheckoutPayment-section">
                <h2>{t('payment.licenseeInformation', { ns: 'checkout' })}</h2>

                <LicenseeInformation />

                {licenseTypes.includes(FontLicenseType.Student) && (
                  <StudentLicenseDetails />
                )}
              </div>

              <div className="CheckoutPayment-section">
                <h2>{t('payment.orderDetails', { ns: 'checkout' })}</h2>

                <OrderDetails
                  order={currentOrder}
                  paymentMethod={paymentMethod}
                  billingCountryCode={values.addressCountryCode}
                  vatNumber={values.vatNumber}
                  disabled={updateCurrentOrderMutation.loading || isSubmitting}
                  paypalDisabled={!isValid}
                  onPayPalAuthorize={(authorization) => {
                    setPaypalPaymentAuthorization(authorization);
                    setPaymentErrorMessage(null);
                    submitForm();
                  }}
                  onPayPalError={(errorMessage) => {
                    setPaypalPaymentAuthorization(null);
                    setPaymentErrorMessage(errorMessage);
                  }}
                  onApplyPromotion={() => {
                    updateCurrentOrder({
                      variables: {
                        input: { promotionCode: values.promotionCode },
                        language,
                      },
                    });
                  }}
                  onLicensesAgreedToChange={(value) => {
                    setFieldValue('licensesAgreedTo', value);
                    setFieldTouched('licensesAgreedTo', true);
                  }}
                  onTermsAndConditionsAgreedToChanged={(value) => {
                    setFieldValue('termsAndConditionsAgreedTo', value);
                    setFieldTouched('termsAndConditionsAgreedTo', true);
                  }}
                />

                {paymentMethod !== 'paypal' && (
                  <Core.Button
                    fullwidth
                    type="submit"
                    disabled={isSubmitting}
                    acting={updateCurrentOrderMutation.loading || isSubmitting}
                    testID="place-order-button"
                  >
                    {t('payment.placeOrder', { ns: 'checkout' })}
                  </Core.Button>
                )}

                <div
                  className="CheckoutPayment-message"
                  dangerouslySetInnerHTML={{
                    __html: t('payment.wireTransferInstructions', {
                      ns: 'checkout',
                    }).toString(),
                  }}
                />
              </div>

              {isSubmitting && (
                <Core.Loader
                  overlay
                  text={t('payment.processing', { ns: 'checkout' })}
                />
              )}
            </form>
          )}
        </Formik>
      </div>
    </ColorBar>
  );
};

const PaymentWithHostedFields = () => (
  <Braintree.ClientProvider>
    <Braintree.HostedFieldsProvider>
      <CheckoutPayment />
    </Braintree.HostedFieldsProvider>
  </Braintree.ClientProvider>
);

export default PaymentWithHostedFields;
